import { useEffect, useRef } from 'react'
import { Alert, Box, Button } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { getRoleDisplayLabel } from '../../lib/roles'
import { getUserFullName } from '../../lib/userUtils'

export function ImpersonationBanner({ onHeightChange }) {
  const { impersonation, userProfile, isViewingAsUser, endImpersonation } = useAuth()
  const navigate = useNavigate()
  const bannerRef = useRef(null)

  useEffect(() => {
    if (!isViewingAsUser || !onHeightChange || !bannerRef.current) return
    const node = bannerRef.current
    const report = () => onHeightChange(node.getBoundingClientRect().height)
    report()
    const observer = new ResizeObserver(report)
    observer.observe(node)
    return () => observer.disconnect()
  }, [isViewingAsUser, onHeightChange, userProfile?.email])

  if (!isViewingAsUser) return null

  const name = getUserFullName(userProfile) || userProfile?.email || 'User'
  const roleLabel = getRoleDisplayLabel(userProfile)
  const impersonatorLabel = getUserFullName(impersonation?.impersonator)
    || impersonation?.impersonator?.email
    || null

  const handleExit = async () => {
    await endImpersonation()
    navigate('/super-admin/people', { replace: true })
  }

  return (
    <Alert
      ref={bannerRef}
      severity="warning"
      sx={{
        borderRadius: 0,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        minHeight: 52,
        zIndex: (theme) => theme.zIndex.modal + 1,
        alignItems: 'flex-start',
        py: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          width: '100%',
          flexWrap: 'wrap',
        }}
      >
        <Box component="span" sx={{ flex: '1 1 200px', minWidth: 0, lineHeight: 1.4 }}>
          Viewing as <strong>{name}</strong>
          {roleLabel ? ` (${roleLabel})` : ''}
          {impersonatorLabel ? ` · started by ${impersonatorLabel}` : ''}
        </Box>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          onClick={handleExit}
          sx={{ flexShrink: 0, minHeight: 44 }}
        >
          Exit view as
        </Button>
      </Box>
    </Alert>
  )
}
